// What the top bar's market block shows on a page, worked out from its URL:
// - "market": a page inside one market; dropdown of markets.
// - "either": Home, Revenue and Inventory Overview, which exist per market
//   and for all markets;
//   dropdown with All markets first. Inventory Overview is one page for
//   both: /inventory/overview, or /inventory/overview/:market_slug.
// - "all": all-markets pages (Purchase Plan, Incoming / PO, incoming order destinations); a plain label.
// - "none": pages about the person or the org (My Work, settings); no block.
export type BlockKind = "market" | "either" | "all" | "none";

// Inventory Overview keeps the market after its own path, not under /m/.
const INVENTORY_OVERVIEW = "/inventory/overview";

const ALL_MARKET_PAGES = ["/purchase/products", "/purchase/incoming", "/purchase/destinations"];

// Sections kept when switching market, longest first so the most specific
// wins. Deeper paths (a plan, a channel) fall back to the section root: those
// records do not exist in the other market.
const SECTIONS = [
  "/plan/top-down", "/plan/product-pricings", "/plan/bottom-up/by-month", "/plan/bottom-up/by-day", "/plan/actuals",
  "/wholesale/customers", "/wholesale/forecast-by-account",
  "/push/review",
  "/warehouses",
  "/revenue/products", "/revenue",
];

const under = (path: string, prefix: string) => path === prefix || path.startsWith(`${prefix}/`);

// Path without query string or trailing slash.
function pathOf(url: string): string {
  const path = url.split("?")[0].replace(/\/+$/, "");
  return path || "/";
}

// The part after /m/:slug for a market URL ("" for the overview), or null.
function marketRest(path: string): string | null {
  const match = path.match(/^\/m\/[^/]+(.*)$/);
  return match ? match[1] : null;
}

export function blockKind(url: string): BlockKind {
  const path = pathOf(url);
  const rest = marketRest(path);
  if (rest !== null) return rest === "" || under(rest, "/revenue") ? "either" : "market";
  if (path === "/dashboard" || under(path, "/revenue") || under(path, INVENTORY_OVERVIEW)) return "either";
  if (ALL_MARKET_PAGES.some((p) => under(path, p))) return "all";
  return "none";
}

// Where picking a market (or All markets, slug null) in the block goes from
// the page at `url`.
export function switchPath(url: string, slug: string | null): string {
  const path = pathOf(url);
  if (under(path, INVENTORY_OVERVIEW)) return slug === null ? INVENTORY_OVERVIEW : `${INVENTORY_OVERVIEW}/${slug}`;
  // On all-markets pages the path itself is the section (/revenue); /dashboard
  // is the all-markets Home, matching a market's overview.
  const rest = marketRest(path) ?? (path === "/dashboard" ? "" : path);

  if (slug === null) {
    if (under(rest, "/revenue/products")) return "/revenue/products";
    if (under(rest, "/revenue")) return "/revenue";
    return "/dashboard";
  }
  const section = SECTIONS.find((s) => under(rest, s));
  return `/m/${slug}${section ?? ""}`;
}
